/**
 * PHOTOS — the biggest hole in the rebuild, and the one the shop notices.
 *
 * Two thousand and forty-two of them came across in the import, and every
 * one still points at the OLD project's storage. That is fine while both
 * exist and is a migration problem the day the old one goes away: the
 * customer page would render broken images for every job before the
 * cutover. Copying the files across is a separate job and it is listed as
 * one; this file deals with new photos, which land in this project.
 *
 * SHRUNK BEFORE UPLOAD, always. A modern phone shoots 4-6MB and a
 * technician takes eight of them under a car on a bad signal. Sending the
 * original is how you get a spinner that never finishes and a photo that
 * never arrives. Resized to 2048px on the long edge at quality 0.85 — the
 * same numbers the old system settled on — a shop photo lands under half a
 * megabyte and loses nothing anybody was looking at.
 *
 * The resize happens on the phone, before a byte leaves it — which also
 * means the slow part happens where there is a progress bar rather than
 * on a connection that might drop.
 */

import { db } from "./client.ts";
import { signedPhoto, thumbPathOf } from "./photolinks.ts";
import { injectGps, readGps } from "./photogps.ts";

export type PhotoKind =
  | "general" | "arrival" | "damage" | "part" | "inspection" | "release" | "authorization";

export const KINDS: Array<{ key: PhotoKind; label: string; what: string }> = [
  { key: "arrival", label: "On arrival", what: "How it looked when it turned up" },
  { key: "damage", label: "The fault", what: "The thing that is wrong" },
  { key: "part", label: "Parts", what: "Old and new, receipts, part numbers" },
  { key: "inspection", label: "Inspection", what: "Points checked" },
  { key: "release", label: "Finished", what: "The work done, before it goes back" },
  { key: "general", label: "Anything else", what: "" },
];

export type Photo = {
  id: string;
  url: string | null;
  path: string | null;
  kind: PhotoKind;
  caption: string | null;
  taken_at: string;
  job_id: string | null;
  vehicle_id: string | null;
};

const SEL = "id,url,path,kind,caption,taken_at,job_id,vehicle_id";
const BUCKET = "job-photos";

/**
 * A stored path becomes a signed link; an imported row already has a URL.
 * "" when there is nothing to show or the link could not be signed.
 */
export async function srcOf(p: Photo): Promise<string> {
  if (p.url) return p.url;
  if (!p.path) return "";
  return signedPhoto(p.path);
}

export async function photosForJob(jobId: string): Promise<Photo[]> {
  const sb = await db();
  const { data, error } = await sb.from("photos").select(SEL)
    // Newest first, the same way the vehicle's own history reads and the
    // same way every other list in here does. The latest photo of a car in
    // pieces is the one somebody opening the job wants.
    .eq("job_id", jobId).order("taken_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Photo[];
}

export async function photosForVehicle(vehicleId: string): Promise<Photo[]> {
  const sb = await db();
  const { data, error } = await sb.from("photos").select(SEL)
    .eq("vehicle_id", vehicleId).order("taken_at", { ascending: false }).limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Photo[];
}

/** The whole shop's roll, newest first. */
export async function recentPhotos(kind?: PhotoKind, limit = 120, offset = 0): Promise<Photo[]> {
  const sb = await db();
  let q = sb.from("photos").select(SEL)
    .order("taken_at", { ascending: false }).range(offset, offset + limit - 1);
  if (kind) q = q.eq("kind", kind);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Photo[];
}

/**
 * Shrink on the phone. Canvas rather than a library, because this is one
 * draw call and a dependency for it would be larger than the code.
 */
export async function shrink(file: File, maxEdge = 2048, quality = 0.85): Promise<File> {
  if (!file || !/^image\//.test(file.type || "")) return file;
  // A GIF is an animation and an SVG is not pixels. Redrawing either onto
  // a canvas throws away the thing that made it worth keeping.
  if (/^image\/(gif|svg)/.test(file.type)) return file;

  let bmp: ImageBitmap | null = null;
  try {
    // imageOrientation FROM-IMAGE is the whole reason this is not three
    // lines. A phone writes the sensor's pixels and an EXIF tag saying
    // which way up they were; drawing to a canvas without honoring that
    // tag bakes in the raw orientation, so every photo a technician takes
    // in portrait arrives on its side. The old system learned this and it
    // is the first thing to carry over.
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });

    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    // Already small enough and already light enough to leave alone —
    // re-encoding a good file only loses detail.
    if (scale === 1 && file.size <= 900 * 1024) return file;

    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bmp, 0, 0, w, h);

    const drawn = await new Promise<Blob | null>((r) => cv.toBlob(r, "image/jpeg", quality));
    // If it came back bigger, the original was already better than this.
    if (!drawn || drawn.size >= file.size) return file;

    // The canvas wrote no EXIF, so the spot the photo was taken went with
    // it. Anthony, 2026-10-27: the location stays, exact. Only lat/lng is
    // written back; see photogps.ts. The original handed back above keeps
    // its own EXIF untouched, so it needs nothing.
    const blob = await injectGps(drawn, await readGps(file));

    return new File([blob], `${(file.name || "photo").replace(/\.[^.]+$/, "")}.jpg`,
      { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    // A format the browser will not decode — HEIC on some desktops. Send
    // the original rather than nothing.
    return file;
  } finally {
    bmp?.close();
  }
}

/** Hex SHA-256 of what is about to be uploaded. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * THE SAME PHOTO, ALREADY ON A DIFFERENT JOB.
 *
 * Found 2026-10-05: one Tahoe engine-bay photo was on four of Brian
 * Huey's jobs, a Traverse and a lawnmower among them, byte for byte. The
 * app copies nothing; the gallery on a phone shows last week's photos
 * first, and one tap put them on the wrong car. The customer page then
 * shows that photo as their car. So the upload stops and asks, and the
 * person decides.
 *
 * Thrown rather than answered with a confirm() in here, because this file
 * knows nothing about screens. The page catches it, asks, and calls again
 * with `allowDuplicate`.
 */
export class DuplicatePhoto extends Error {
  constructor(readonly jobNumber: string | null, readonly jobTitle: string | null) {
    super(`This photo is already on ${jobNumber ? `job #${jobNumber}` : "another job"}${
      jobTitle ? ` (${jobTitle})` : ""}.`);
  }
}

/**
 * The other job holding these exact bytes, if any. Null when there is
 * none, and null when the column is not there yet (0194 not applied), so
 * a missing migration costs the warning and never the upload.
 */
async function alreadyOn(
  hash: string, jobId: string,
): Promise<{ number: string | null; title: string | null } | null> {
  const sb = await db();
  const { data, error } = await sb.from("photos")
    .select("job_id, jobs(job_number, title)")
    .eq("content_sha256", hash).neq("job_id", jobId).limit(1);
  if (error || !data?.length) return null;
  type J = { job_number: string | null; title: string | null };
  // One job per photo, but the generated types call the embed a list.
  const raw = (data[0] as unknown as { jobs: J | J[] | null }).jobs;
  const j = Array.isArray(raw) ? raw[0] : raw;
  return { number: j?.job_number ?? null, title: j?.title ?? null };
}

/**
 * THE SQUARE FOR THE LISTS. A 144px centre crop, made on the phone from the
 * bytes that were just stored, so the job and quote lists never ask storage
 * to resize anything (storage bills that per photo; see signedThumb). About
 * 5KB. Null when the browser cannot draw it, and the list then falls back
 * to the full photo.
 */
export async function makeThumb(blob: Blob, size = 144, quality = 0.62): Promise<Blob | null> {
  let bmp: ImageBitmap | null = null;
  try {
    bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
    const side = Math.min(bmp.width, bmp.height);
    const cv = document.createElement("canvas");
    cv.width = size; cv.height = size;
    const ctx = cv.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, size, size);
    return await new Promise<Blob | null>((r) => cv.toBlob(r, "image/jpeg", quality));
  } catch {
    return null;
  } finally {
    bmp?.close();
  }
}

/**
 * One photo, onto a job. Storage first, then the row — so a failed upload
 * never leaves a record pointing at a file that is not there. The reverse
 * order would give the customer's page a broken image.
 */
export async function addPhoto(
  jobId: string, vehicleId: string | null, file: File, kind: PhotoKind, caption: string,
  opts: { allowDuplicate?: boolean } = {},
): Promise<Photo> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Cannot tell which shop you belong to. Sign in again.");

  const blob = await shrink(file);
  // shrink() hands back the ORIGINAL when it cannot improve on it — a PNG
  // already under the limit, a HEIC the browser will not decode. Claiming
  // image/jpeg for those would store a file whose bytes disagree with its
  // type, which browsers mostly survive and thumbnailers mostly do not.
  const type = blob.type || file.type || "image/jpeg";

  // The bucket takes jpeg, png and webp. Anything else — a HEIC the
  // browser would not decode, so shrink handed the original back — is
  // refused by storage with a message about mime types that means nothing
  // to somebody standing at a car. Say the useful version instead.
  const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
  if (!ALLOWED.includes(type)) {
    throw new Error(
      "This phone saved the photo in a format the system cannot read. " +
      "In Settings > Camera, set Formats to Most Compatible, then take it again.");
  }
  const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";

  // Hashed after the shrink, because the shrink is deterministic and these
  // are the bytes that are stored. The same gallery photo picked twice
  // comes out of it identical every time.
  const hash = await sha256Hex(blob);
  if (!opts.allowDuplicate) {
    const other = await alreadyOn(hash, jobId);
    if (other) throw new DuplicatePhoto(other.number, other.title);
  }

  // Job first in the path so everything about one car sits together, and
  // a timestamp so two photos in the same second do not collide.
  const stamp = `${Date.now()}-${Math.round(performance.now())}`;
  const path = `${accountId}/${jobId}/${kind}/${stamp}.${ext}`;

  const up = await sb.storage.from(BUCKET).upload(path, blob, {
    contentType: type, upsert: false,
  });
  if (up.error) throw new Error(`The photo did not upload: ${up.error.message}`);

  const row = {
    account_id: accountId, job_id: jobId, vehicle_id: vehicleId,
    path, kind, caption: caption.trim() || null,
    taken_by: me.user?.id ?? null,
  };
  let { data, error } = await sb.from("photos")
    .insert({ ...row, content_sha256: hash }).select(SEL).single();
  // Before 0194 the column does not exist. The photo matters more than its
  // fingerprint, so it goes in without one rather than not at all.
  if (error && /content_sha256/.test(error.message)) {
    ({ data, error } = await sb.from("photos").insert(row).select(SEL).single());
  }

  if (error) {
    // The row is what makes the file findable. Without it the upload is
    // litter, so take it back out rather than leave it orphaned.
    await sb.storage.from(BUCKET).remove([path]);
    throw new Error(error.message);
  }

  // The list square. Best effort: the photo is already saved, and a
  // missing square only means the list shows the full photo instead.
  const small = await makeThumb(blob);
  if (small) {
    const t = await sb.storage.from(BUCKET).upload(thumbPathOf(path), small,
      { contentType: "image/jpeg", upsert: false });
    if (t.error) console.warn(`photo thumb: not stored for ${path}`, t.error.message);
  }
  return data as unknown as Photo;
}

export async function setCaption(id: string, caption: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("photos")
    .update({ caption: caption.trim() || null }).eq("id", id);
  if (error) throw new Error(error.message);
}

/** Row and file both, and the row first so nothing is ever left dangling. */
export async function removePhoto(p: Photo): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("photos").delete().eq("id", p.id);
  if (error) throw new Error(error.message);
  if (p.path) await sb.storage.from(BUCKET).remove([p.path, thumbPathOf(p.path)]);
}
